import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, InputBase, Stack, Typography } from '@mui/material';
import { livyJson } from 'src/lib/livyApi';
import { livyEvent } from './LivyGreeting';
import { LivyMarkdown } from './LivyMarkdown';
import { LivyPageFrame } from './LivyPageFrame';
/** Video hosts whose players are built to be embedded; anything else stays a link. */
const EMBEDDABLE = /^https:\/\/(play\.hubspotvideo\.com|www\.youtube\.com\/embed|player\.vimeo\.com|www\.loom\.com\/embed|fast\.wistia\.net)\//;

/** The knowledge-base sync turns each video embed into a "[Watch the video](url)" link. */
export const videosIn = markdown => [...markdown.matchAll(/\[Watch the video\]\((https:[^)\s]+)\)/g)].map(m => m[1]).filter(u => EMBEDDABLE.test(u));

/** The article's videos, played in place. Sandboxed: the player needs scripts, nothing more. */
export function LivyVideos(_ref) {
  let body = _ref.body,
    title = _ref.title;
  return /*#__PURE__*/React.createElement(React.Fragment, null, videosIn(body).map(src => /*#__PURE__*/React.createElement(Box, {
    key: src,
    sx: {
      position: 'relative',
      pt: '56.25%',
      mb: '14px',
      borderRadius: '10px',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    component: "iframe",
    src: src,
    title: "".concat(title, " video"),
    allow: "fullscreen; picture-in-picture",
    sandbox: "allow-scripts allow-same-origin allow-presentation",
    sx: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      border: 0
    }
  }))));
}
const excerpt = function () {
  let text = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return text.replace(/[#*_`>[\]()!-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 120);
};
/**
 * The help center, inside Livy: search or browse the articles and watch the videos here, rather than
 * in a separate site. Reads the audience-filtered /kb, so a customer never sees an internal article.
 */
export function LivyHelp(_ref2) {
  let _ref2$initialQuery = _ref2.initialQuery,
    initialQuery = _ref2$initialQuery === void 0 ? '' : _ref2$initialQuery,
    _ref2$initialArticle = _ref2.initialArticle,
    initialArticle = _ref2$initialArticle === void 0 ? '' : _ref2$initialArticle,
    onAsk = _ref2.onAsk,
    _ref2$recent = _ref2.recent,
    recent = _ref2$recent === void 0 ? [] : _ref2$recent,
    onForget = _ref2.onForget,
    _ref2$onboarding = _ref2.onboarding,
    onboarding = _ref2$onboarding === void 0 ? false : _ref2$onboarding,
    billingType = _ref2.billingType,
    supportFormUrl = _ref2.supportFormUrl,
    _ref2$supportChat = _ref2.supportChat,
    supportChat = _ref2$supportChat === void 0 ? false : _ref2$supportChat,
    _ref2$memory = _ref2.memory,
    memory = _ref2$memory === void 0 ? [] : _ref2$memory,
    onRemoveFact = _ref2.onRemoveFact;
  const _useState = useState(initialQuery),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    articles = _useState4[0],
    setArticles = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    article = _useState6[0],
    setArticle = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    error = _useState8[0],
    setError = _useState8[1];
  const _useState9 = useState('idle'),
    _useState0 = _slicedToArray(_useState9, 2),
    forgot = _useState0[0],
    setForgot = _useState0[1];
  const _useState1 = useState(null),
    _useState10 = _slicedToArray(_useState1, 2),
    next = _useState10[0],
    setNext = _useState10[1];
  const _useState11 = useState(memory),
    _useState12 = _slicedToArray(_useState11, 2),
    facts = _useState12[0],
    setFacts = _useState12[1];
  useEffect(() => {
    if (!onboarding) return undefined;
    let live = true;
    livyJson('/onboarding/next').then(r => live && setNext(r.next)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [onboarding]);
  useEffect(() => setQuery(initialQuery), [initialQuery]);
  useEffect(() => {
    let live = true;
    setArticles(null);
    setError(false);
    const q = query.trim();
    const timer = setTimeout(() => livyJson("".concat(q ? "/kb/search?limit=20&q=".concat(encodeURIComponent(q), "&") : '/kb?', "type=").concat(encodeURIComponent(billingType !== null && billingType !== void 0 ? billingType : ''))).then(r => live && setArticles(r.articles)).catch(() => live && setError(true)), q ? 250 : 0);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, billingType]);
  const open = slug => livyJson("/kb/".concat(encodeURIComponent(slug))).then(r => {
    setArticle(r.article);
    void livyEvent('help_opened', slug);
  }).catch(() => setError(true));

  // Titles come from the browse list, so a slug that no longer exists simply does not show.
  const recentArticles = query.trim() || forgot === 'done' ? [] : recent.map(slug => articles === null || articles === void 0 ? void 0 : articles.find(a => a.slug === slug)).filter(a => Boolean(a));
  const handleForget = async () => {
    if (!onForget) return;
    setForgot('busy');
    try {
      await onForget();
      setForgot('done');
    } catch (_unused) {
      setForgot('idle');
    }
  };
  useEffect(() => {
    if (initialArticle) void open(initialArticle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialArticle]);
  if (article) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: '20px',
        py: '14px'
      }
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      onClick: () => setArticle(null),
      sx: {
        textTransform: 'none',
        ml: '-8px',
        mb: '6px'
      }
    }, "\u2190 All help"), /*#__PURE__*/React.createElement(Typography, {
      component: "h2",
      sx: {
        fontSize: 18,
        fontWeight: 600,
        mb: '10px'
      }
    }, article.title), article.html ? /*#__PURE__*/React.createElement(LivyPageFrame, {
      html: article.html,
      title: article.title
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LivyVideos, {
      body: article.body,
      title: article.title
    }), /*#__PURE__*/React.createElement(LivyMarkdown, {
      content: article.body
    })), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "outlined",
      onClick: () => onAsk("Help me with: ".concat(article.title)),
      sx: {
        textTransform: 'none',
        mt: '8px',
        borderRadius: '10px'
      }
    }, "Ask Livy about this"));
  }
  return /*#__PURE__*/React.createElement(Stack, {
    sx: {
      flex: 1,
      minHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      pt: '12px',
      pb: '8px'
    }
  }, /*#__PURE__*/React.createElement(InputBase, {
    value: query,
    onChange: e => setQuery(e.target.value),
    placeholder: "Search help articles and videos",
    inputProps: {
      'aria-label': 'Search help'
    },
    sx: {
      width: '100%',
      px: '12px',
      py: '6px',
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
      fontSize: 14
    }
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      px: '8px',
      pb: '12px'
    },
    role: "list",
    "aria-label": "Help articles"
  }, error && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      p: '12px'
    }
  }, "Help is unavailable right now. Try again in a moment."), !error && !articles && /*#__PURE__*/React.createElement(Box, {
    sx: {
      p: '16px',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, {
    size: 20
  })), (articles === null || articles === void 0 ? void 0 : articles.length) === 0 && /*#__PURE__*/React.createElement(Stack, {
    spacing: "8px",
    sx: {
      p: '12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No article matches that yet."), /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "outlined",
    onClick: () => onAsk(query),
    sx: {
      textTransform: 'none',
      alignSelf: 'flex-start'
    }
  }, "Ask Livy instead")), next && !query.trim() && /*#__PURE__*/React.createElement(Button, {
    onClick: () => void open(next.slug),
    "aria-label": "Next setup step: ".concat(next.title),
    sx: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      textTransform: 'none',
      color: 'text.primary',
      border: 1,
      borderColor: 'primary.main',
      borderRadius: '10px',
      px: '12px',
      py: '10px',
      mb: '8px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600,
      color: 'primary.main'
    }
  }, "Next", next.propertyAddress ? " for ".concat(next.propertyAddress.split(',')[0]) : ''), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600
    }
  }, next.title)), recentArticles.length > 0 && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600,
      color: 'text.secondary',
      px: '10px',
      pt: '4px'
    }
  }, "Recently opened"), recentArticles.map(a => /*#__PURE__*/React.createElement(Button, {
    key: "recent-".concat(a.slug),
    role: "listitem",
    onClick: () => void open(a.slug),
    sx: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      textTransform: 'none',
      color: 'text.primary',
      borderRadius: '10px',
      px: '10px',
      py: '6px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600
    }
  }, a.title))), recentArticles.length > 0 && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12,
      fontWeight: 600,
      color: 'text.secondary',
      px: '10px',
      pt: '10px'
    }
  }, "All help"), articles === null || articles === void 0 ? void 0 : articles.map(a => /*#__PURE__*/React.createElement(Button, {
    key: a.slug,
    role: "listitem",
    onClick: () => void open(a.slug),
    sx: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      textTransform: 'none',
      color: 'text.primary',
      borderRadius: '10px',
      px: '10px',
      py: '8px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600
    }
  }, a.title), (a.excerpt || a.snippet) && /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary',
      mt: '2px'
    }
  }, excerpt(a.excerpt || a.snippet))))), supportChat && /*#__PURE__*/React.createElement(Button, {
    onClick: () => onAsk('I have a question for Livable support.'),
    sx: {
      display: 'block',
      textAlign: 'left',
      textTransform: 'none',
      color: 'text.primary',
      mx: '10px',
      mb: '8px',
      px: '10px',
      py: '8px',
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600
    }
  }, "Ask support"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary',
      mt: '2px'
    }
  }, "Billing, residents, payouts or AppFolio. Livy answers from your account and hands anything else to Livable's team.")), supportFormUrl && /*#__PURE__*/React.createElement(Button, {
    component: "a",
    href: supportFormUrl,
    target: "_blank",
    rel: "noopener noreferrer",
    sx: {
      display: 'block',
      textAlign: 'left',
      textTransform: 'none',
      color: 'text.primary',
      mx: '10px',
      mb: '8px',
      px: '10px',
      py: '8px',
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 600
    }
  }, "Account and plan changes"), /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 12.5,
      color: 'text.secondary',
      mt: '2px'
    }
  }, "Upgrade, add bill automation, downgrade, pause or cancel, or reach support. Opens Livable's form.")), onForget && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '10px',
      borderTop: 1,
      borderColor: 'divider',
      fontSize: 12,
      color: 'text.secondary'
    }
  }, forgot === 'done' ? 'Done. Livy has forgotten your history.' : /*#__PURE__*/React.createElement(React.Fragment, null, facts.length > 0 && /*#__PURE__*/React.createElement(Box, {
    component: "ul",
    "aria-label": "What Livy has learned about you",
    sx: {
      m: 0,
      mb: '6px',
      pl: '18px'
    }
  }, facts.map(f => /*#__PURE__*/React.createElement("li", {
    key: f
  }, f, ' ', onRemoveFact && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    "aria-label": "Forget: ".concat(f),
    onClick: () => void onRemoveFact(f).then(() => setFacts(list => list.filter(x => x !== f))).catch(() => undefined),
    sx: {
      textTransform: 'none',
      fontSize: 12,
      p: 0,
      minWidth: 0,
      verticalAlign: 'baseline'
    }
  }, "Forget")))), "What Livy remembers: what it has learned from your conversations", facts.length ? ' (above)' : '', ", which reports you run and the range you usually ask for, the help articles you open, and the reports you star.", ' ', /*#__PURE__*/React.createElement(Button, {
    size: "small",
    disabled: forgot === 'busy',
    onClick: handleForget,
    sx: {
      textTransform: 'none',
      fontSize: 12,
      p: 0,
      minWidth: 0,
      verticalAlign: 'baseline'
    }
  }, "Forget my history"))));
}